import EditNoteIcon from "@mui/icons-material/EditNote";
import EqualizerIcon from "@mui/icons-material/Equalizer";
import LogoutIcon from "@mui/icons-material/Logout";
import PersonIcon from "@mui/icons-material/Person";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import { Tooltip } from "@mui/material";
import i18next, { t } from "i18next";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReactSelect from "react-select";
import useMobileView from "../../hooks/mobileView";
import handleLogout from "../../redux/logout";
import { fetchUserInfo } from "../../redux/me";
import { setCredentials } from "../../redux/slices/authSlice";
import { connectPaymentStatusSocket } from "../sockets/socket";
import { formatNumber } from "../utils/formatNumber";

function Header({ handleDrawer }) {
  const accessToken = useSelector((state) => state?.auth).accessToken;

  const userInfo = useSelector((state) => state?.auth).userInfo;
  const navigate = useNavigate();
  const isMobile = useMobileView();
  const dispatch = useDispatch();
  const [ws, setWs] = useState(null);

  // Check Payment Status and navigate
  const paymentStatusCheck = (status, updated) => {
    if (status?.toLowerCase() == "active" && updated == false) {
      navigate("/dashboard");
    }
  };

  // Load User Info
  const loadUserInfo = async () => {
    if (accessToken) {
      try {
        const userInfoResult = await fetchUserInfo(accessToken);
        dispatch(
          setCredentials({
            userInfo: userInfoResult.data,
            accessToken: accessToken,
          })
        );
      } catch (error) {
        console.error("Failed to fetch user info:", error);
      }
    }
  };
  useEffect(() => {
    loadUserInfo();
  }, []);
  const connectSocket = () => {
    try {
      const socket = connectPaymentStatusSocket(userInfo.unique_id); // Connect to WebSocket
      setWs(socket);

      socket.onopen = () => {
        loadUserInfo();
      };

      socket.onmessage = (e) => {
        const data = JSON.parse(e.data);
        console.log(data, "Socket");
        // Update user info
        dispatch(
          setCredentials({
            accessToken: accessToken,
            userInfo: {
              ...userInfo,
              plan: data?.plan,
              plan_period: data?.plan_period,
              payment_status:
                data?.payment_status?.toLowerCase() === "none"
                  ? null
                  : data?.payment_status,
            },
          })
        );

        // Navigation if status changed
        paymentStatusCheck(
          data?.payment_status?.toLowerCase() === "none"
            ? null
            : data?.payment_status,
          data?.updated
        );
      };

      socket.onerror = (e) => {};

      socket.onclose = (e) => {
        connectSocket();
      };
    } catch (error) {
      connectSocket();
    }
  };

  const languageOptions = [
    {
      value: "fr",
      label: (
        <div className="flex items-center gap-2">
          🇫🇷 <span className="text-sm">Français</span>
        </div>
      ),
    },
    {
      value: "en",
      label: (
        <div className="flex items-center gap-2">
          🇬🇧 <span className="text-sm">English</span>
        </div>
      ),
    },
  ];

  const handleLanguageChange = (selectedOption) => {
    const selectedLang = selectedOption?.value;
    if (selectedLang) {
      console.log("CHANGE LANGUAGE");
      i18next.changeLanguage(selectedLang);
    }
  };

  useEffect(() => {
    // connect to socket
    if (userInfo && !ws) {
      connectSocket();
    }
  }, [userInfo, ws]);

  return (
    <>
      <nav className="bg-white fixed border-gray-200 z-40 w-full shadow-2">
        <div className="flex flex-wrap items-center justify-between mx-auto p-4 sm:pl-9 pl-6 sm:pr-7 pr-2">
          <div className="flex flex-wrap items-center">
            <button
              data-collapse-toggle="navbar-dropdown"
              type="button"
              className="items-center p-2 w-10 h-10 justify-center text-sm sm:ml-7 text-[#0000008a] xl:hidden block"
              aria-controls="navbar-dropdown"
              aria-expanded="false"
              onClick={handleDrawer}
            >
              <span className="sr-only">Open main menu</span>
              <svg
                className="w-5 h-5"
                aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 17 14"
              >
                <path
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M1 1h15M1 7h15M1 13h15"
                />
              </svg>
            </button>
          </div>

          <div className="flex items-center sm:gap-6 gap-3 justify-between">
            <div className="hidden md:grid sm:grid-cols-2 grid-cols-1 gap-2">
              <div className="flex flex-col justify-center items-center">
                <Tooltip title={t("Article-Tokens", "Article Tokens")}>
                  <div className="rounded-lg bg-blue-200 shadow-md h-7 text-white flex items-center justify-center flex-row p-4 gap-1">
                    <EditNoteIcon />
                    <p className="text-lg">
                      {formatNumber(
                        userInfo?.blog_posts_tokens
                          ? userInfo?.blog_posts_tokens
                          : 0
                      )}
                    </p>
                  </div>
                </Tooltip>
              </div>
              <div className="flex  flex-col justify-center  items-center">
                <Tooltip
                  title={t("SEO-Analysis-Tokens", "SEO Analysis Tokens")}
                >
                  <div className="rounded-lg bg-blue-400 shadow-md h-7 text-white flex flex-row items-center justify-center p-4 gap-1">
                    <EqualizerIcon />
                    <p className="text-lg">
                      {formatNumber(
                        userInfo?.seo_analysis_tokens
                          ? userInfo?.seo_analysis_tokens
                          : 0
                      )}
                    </p>
                  </div>
                </Tooltip>
              </div>
            </div>
            <div className="relative flex justify-center items-center group">
              <button
                className="hover:bg-blue-200 *: bg-blue-100 text-white disabled:bg-slate-200 disabled:text-slate-400 font-light px-5 py-[6px] rounded flex items-center"
                disabled={userInfo?.plan === "Demo"}
                onClick={() => navigate("/pricing")}
              >
                {userInfo?.plan === "Ultimate"
                  ? t("Manage-plan", "Gérer mon abonnement")
                  : t("Upgrade-plan", "Mettre à niveau mon abonnement")}
              </button>

              {/* Tooltip on hover */}
              {userInfo.plan === "Demo" && (
                <div className="absolute left-1/2 transform -translate-x-1/2 top-full mt-1 hidden group-hover:flex flex-col items-center">
                  <div
                    className="w-3 h-2 bg-slate-600/80"
                    style={{ clipPath: "polygon(50% 0%, 0% 100%, 100% 100%)" }}
                  ></div>
                  <span className="bg-slate-600/80 text-white text-sm rounded-md py-1 px-2 min-w-36 text-center shadow-lg">
                    {t(
                      "Not available in demo mode",
                      "Indisponible en mode démo"
                    )}
                  </span>
                </div>
              )}
            </div>

            <ReactSelect
              options={languageOptions}
              value={languageOptions.find(
                (opt) => opt.value === i18next.language
              )}
              onChange={handleLanguageChange}
              isSearchable={false}
              menuPlacement="top"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "32px",
                  fontSize: "14px",
                  paddingLeft: "4px",
                }),
                menu: (base) => ({
                  ...base,
                  zIndex: 9999,
                }),
              }}
            />

            <button
              title="Centre d'aide"
              className="h-8 w-8 flex items-center justify-center hover:text-blue-50 bg-Lightwhite rounded-full  text-gray font-light"
              onClick={() => navigate("/help")}
            >
              <QuestionMarkIcon sx={{ fontSize: "20px" }} />
            </button>

            <button
              title="Mon compte"
              className="h-8 w-8 flex items-center justify-center hover:text-blue-50 bg-Lightwhite rounded-full  text-gray font-light"
              onClick={() => navigate("/profile")}
            >
              <PersonIcon sx={{ fontSize: "20px" }} />
            </button>

            <button
              title="Se déconnecter"
              className="h-8 w-8 flex items-center justify-center hover:text-blue-50 bg-Lightwhite rounded-full  text-gray font-light"
              onClick={() => handleLogout(dispatch)}
            >
              <LogoutIcon
                sx={{
                  fontSize: "20px",
                }}
              />
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}
export default Header;
